import Tooltip from '@mui/material/Tooltip';
import { MOMENTS_COACH_FAQS_URL } from '@shared/constants';
import {
  Box,
  Card,
  CardContent,
  Icon,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@shared/ui';
import { getProxiedAvatarUrl, getStoredAccessToken } from '@shared/utils/avatar';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { analytics } from '../../services/analytics';
import { type TeamMember, useGetTeamQuery } from '../../store/api/apiSlice';
import { MemberCard, MembersWelcomeModal } from './components';
import {
  buildMemberListView,
  getPreferredMemberListFilter,
  MEMBER_LIST_FILTERS,
  type MemberListFilter,
} from './memberListGroups';
import { getMemberDisplayName } from './types';

const FILTER_LABELS: Record<MemberListFilter, string> = {
  'active-and-chem': 'Active',
  'past-members': 'Past Members',
  'past-chem-calls': 'Past Chem Calls',
};

interface MemberListSectionProps {
  title?: string;
  members: TeamMember[];
  onSelectMember: (personId: string) => void;
}

const MemberListSection: FC<MemberListSectionProps> = ({ title, members, onSelectMember }) => {
  if (members.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {title && (
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: 'Basis Grotesque',
            fontWeight: 700,
            letterSpacing: '0.04em',
            color: 'cement.400',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {members.map((member) => (
          <MemberCard
            key={member.person.id}
            name={getMemberDisplayName(member)}
            email={member.person.primary_email}
            companyName={member.org_name}
            avatarUrl={getProxiedAvatarUrl(member.person.id, getStoredAccessToken())}
            firstName={member.person.given_name}
            lastName={member.person.family_name}
            sessionCount={member.session_count}
            onClick={() => onSelectMember(member.person.id)}
          />
        ))}
      </Box>
    </Box>
  );
};

/**
 * MemberListPage - "Select a Member" landing page
 *
 * Shows members from the coach's canonical relationships.
 * Coaches can click a member to view their Sessions and artifacts.
 */
const MemberListPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedFilter, setSelectedFilter] = useState<MemberListFilter>('active-and-chem');

  const {
    data: teamData,
    isLoading: isLoadingTeam,
  } = useGetTeamQuery();
  const trackedLocationRef = useRef<string | null>(null);

  const members = teamData?.members ?? [];
  const userPickedFilter = useRef(false);
  const resolvedSelectedFilter = userPickedFilter.current
    ? selectedFilter
    : getPreferredMemberListFilter(members, selectedFilter);

  useEffect(() => {
    if (resolvedSelectedFilter !== selectedFilter) {
      setSelectedFilter(resolvedSelectedFilter);
    }
  }, [resolvedSelectedFilter, selectedFilter]);

  const selectedView = useMemo(
    () => buildMemberListView(members, resolvedSelectedFilter),
    [members, resolvedSelectedFilter]
  );

  // Analytics tracking for page views
  useEffect(() => {
    if (trackedLocationRef.current === location.key || isLoadingTeam) return;
    if (!teamData?.members) return;
    const props: Record<string, unknown> = {
      screen: 'member_list',
      route: '/members',
      member_count: selectedView.members.length,
    };
    analytics.viewed('Viewed member list', 'member_list', '/members', props, {
      dedupeKey: `viewed-member-list:${location.key}`,
    });
    trackedLocationRef.current = location.key;
  }, [isLoadingTeam, location.key, selectedView.members.length, teamData?.members]);

  const handleMemberClick = (personId: string) => {
    analytics.track('Selected member', {
      member_id: personId,
      from_screen: 'member_list',
    });
    navigate(`/members/${personId}`);
  };

  const hasMembers = members.length > 0;
  const isEmptyState = !isLoadingTeam && !hasMembers;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'sunny.100',
        width: '100%',
      }}
    >
      <MembersWelcomeModal isEmptyState={isEmptyState} />
      <Box sx={{ maxWidth: 800, mx: 'auto', px: 3, py: 3 }}>
        {/* Page Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Basis Grotesque',
              fontWeight: 700,
              fontSize: '32px',
              lineHeight: 1.2,
              color: 'cement.500',
            }}
          >
            Select a Member
          </Typography>
          <Tooltip
            arrow
            placement="right"
            title="This gives Moments the right context to understand who we’re talking about"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'rgba(97, 97, 97, 0.9)',
                  borderRadius: '8px',
                  px: 1,
                  py: 0.5,
                  fontFamily: 'Basis Grotesque',
                  fontWeight: 500,
                  fontSize: '10px',
                  lineHeight: '14px',
                },
              },
              arrow: {
                sx: {
                  color: 'rgba(97, 97, 97, 0.9)',
                },
              },
            }}
          >
            <IconButton aria-label="Member selection info" size="small" sx={{ p: 0.25 }}>
              <Icon sx={{ fontSize: 18, color: 'cement.400' }}>info_outlined</Icon>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Loading State */}
        {isLoadingTeam && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton variant="rounded" height={60} />
            <Skeleton variant="rounded" height={60} />
            <Skeleton variant="rounded" height={60} />
          </Box>
        )}

        {/* Member List */}
        {!isLoadingTeam && hasMembers && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Tabs
              aria-label="Member relationship filters"
              onChange={(_, value: MemberListFilter) => {
                userPickedFilter.current = true;
                setSelectedFilter(value);
              }}
              value={resolvedSelectedFilter}
              variant="scrollable"
            >
              {MEMBER_LIST_FILTERS.map((filter) => (
                <Tab key={filter} label={FILTER_LABELS[filter]} value={filter} />
              ))}
            </Tabs>

            {selectedView.members.length === 0 ? (
              <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent sx={{ textAlign: 'center', py: 6, px: 4 }}>
                  {resolvedSelectedFilter === 'active-and-chem' ? (
                    <>
                      <Typography sx={{ fontSize: 48, mb: 2 }}>🧐</Typography>
                      <Typography variant="body1" color="text.secondary">
                        There are currently no active members to show.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography sx={{ fontSize: 48, mb: 2 }}>
                        {resolvedSelectedFilter === 'past-chem-calls' ? '🤷🤷‍♀️🤷‍♂️' : '🦗🦗🦗'}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {resolvedSelectedFilter === 'past-chem-calls'
                          ? 'No past chemistry calls found.'
                          : 'No past members to show.'}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            ) : (
              <MemberListSection
                members={selectedView.members}
                onSelectMember={handleMemberClick}
              />
            )}
          </Box>
        )}

        {/* Empty State - No members */}
        {!isLoadingTeam && !hasMembers && (
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center', py: 6, px: 4 }}>
              <Typography sx={{ fontSize: 48, mb: 2 }}>🤔</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'cement.500' }}>
                Hmm, I can't seem to find anyone.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                You must enable transcriptions to use Moments.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                To transcribe, click "Use Gemini to take notes" before you join a coaching call.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                <Box
                  component="a"
                  href={MOMENTS_COACH_FAQS_URL}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    '&:hover': { color: 'cement.600' },
                  }}
                >
                  Learn more on our FAQs page
                </Box>
                .
              </Typography>
              <Typography variant="body1" color="text.secondary">
                If you believe this is an error try refreshing and{' '}
                <a
                  href="mailto:coachsupport@mento.co"
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  let us know
                </a>
                .
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default MemberListPage;
